import type { ReactNode } from 'react';
import type { RewardedPlacement } from '@/api/types';
import { useAuth } from '@/stores/auth';
import { Button, type Accent } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { FILTER_PASS_DAILY, FILTER_PASS_MINUTES, FRIEND_BONUS, FRIEND_BONUS_DAILY, useRewardedAd, type RewardedNotice, type RewardedOutcome } from './rewarded';
import { useRewardedAvailable } from './useAds';

export function RewardedNoticeLine({ notice, className }: { notice: RewardedNotice | null; className?: string }) {
  if (!notice) return null;
  return (
    <p
      role={notice.tone === 'error' ? 'alert' : 'status'}
      className={cn('text-sm', notice.tone === 'success' ? 'font-semibold text-go' : notice.tone === 'error' ? 'text-danger' : 'text-muted', className)}
      data-testid="ad-rewarded-notice"
      data-kind={notice.kind}
    >
      {notice.text}
    </p>
  );
}

function PlayGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden>
      <path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" />
    </svg>
  );
}

interface OfferProps {
  placement: RewardedPlacement;
  testId: string;
  label: ReactNode;
  hint?: ReactNode;
  /** Show the button (caps not reached). The notice still shows after it hides. */
  available: boolean;
  dropId?: string;
  accent?: Accent;
  variant?: 'solid' | 'outline' | 'subtle';
  onDone?: (o: RewardedOutcome) => void;
  className?: string;
}

/** One "Watch an ad for …" offer. Only renders when rewarded ads exist for this person here. */
export function RewardedOffer({ placement, testId, label, hint, available, dropId, accent = 'paper', variant = 'outline', onDone, className }: OfferProps) {
  const ads = useRewardedAvailable();
  const { run, busy, notice } = useRewardedAd(ads, placement, { dropId, onDone });
  if (!ads) return null;
  if (!available && !notice) return null;
  return (
    <div className={cn('space-y-2', className)}>
      {available && (
        <>
          <Button size="sm" accent={accent} variant={variant} onClick={() => void run()} loading={busy} data-testid={testId}>
            <PlayGlyph /> {label}
          </Button>
          {hint && <p className="text-[13px] text-muted">{hint}</p>}
        </>
      )}
      <RewardedNoticeLine notice={notice} />
    </div>
  );
}

/** Locked Filters sheet: "Watch an ad · 30 min of filters" (2 a day, not past 60 min). */
export function FilterPassOffer({ extend = false, className }: { extend?: boolean; className?: string }) {
  const me = useAuth((s) => s.me);
  const used = me?.rewards_today?.filter_pass ?? 0;
  const left = Math.max(0, FILTER_PASS_DAILY - used);
  return (
    <RewardedOffer
      className={className}
      placement="filter_pass"
      testId="ad-rewarded-filter-pass"
      available={left > 0}
      label={extend ? `Watch an ad · +${FILTER_PASS_MINUTES} min` : `Watch an ad · ${FILTER_PASS_MINUTES} min of filters`}
      hint={
        <>
          {left} of {FILTER_PASS_DAILY} left today. The ad must play to the end. Ads are usually 15–60 seconds.
        </>
      }
    />
  );
}

/** Friend-request daily limit: "Watch an ad for +5 today" (once a day). */
export function FriendRequestsOffer({ onDone }: { onDone?: (o: RewardedOutcome) => void }) {
  const me = useAuth((s) => s.me);
  const left = Math.max(0, FRIEND_BONUS_DAILY - (me?.rewards_today?.friend_requests ?? 0));
  return (
    <RewardedOffer
      className="mt-2"
      placement="friend_requests"
      testId="ad-rewarded-friend-requests"
      available={left > 0}
      label={`Watch an ad for +${FRIEND_BONUS} today`}
      hint="Once a day. The ad must play to the end."
      variant="subtle"
      onDone={onDone}
    />
  );
}
